import createEmojiPlugin from '@draft-js-plugins/emoji';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormProvider, useForm } from 'react-hook-form';
import { db, storage } from 'utils/firebase';
// mui components
import { Avatar, Button, Collapse, Grid, IconButton, Typography } from '@mui/material';
// components
import DefaultAvatar from 'components/default-avatar';
import UploadedFiles from 'components/uploaded-files';
import UploadedImages from 'components/uploaded-images';
import TextEditor from '../text-editor';
// custom hooks
import { StatusValue } from 'components/activity-feed/components/upload-file/components/Dropzone';
import { TranscodingJob, useFileUpload } from 'hooks/useFileUpload';
import { useDeleteUnsupportedEmojies } from './hooks/useDeleteUnsupportedEmojies';
// types
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import { IStyles } from 'redux-store/types';
import { IUploadingFiles } from 'utils/types';
// helpers
import {
  convertHtml,
  createDraftContent,
  createMentionToContentHelper,
  createMentionsArray,
  getHashtagsArray,
  getImageBySize,
} from 'utils/helpers';
import { getMimeTypeFromFileName } from 'utils/helpers/fileCabinetHelpers';
import { activityFeedSchema, postReplySchema } from './validation';
// icons
import AttachFileIcon from 'assets/icons/buttons/attach-file-icon.svg';
import AttachImageIcon from 'assets/icons/buttons/attach-image-icon.svg';
import ChevronIcon from 'assets/icons/buttons/chevron-down.svg';
import EmojiIcon from 'assets/icons/buttons/emoji-icon.svg';
// types
// styles
import { IMediaItem } from '_interfaces/types/post.types';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import { MentionIndicator } from 'components/mention-indicator';
import { useSearchParams } from 'react-router-dom';
import { useGetMeQuery } from 'redux-store/api/auth';
import { newPostStyles } from './styles/styles';
import { selectedProjectAtom } from 'atoms';
import { useAtomValue } from 'jotai';

export type TAction = 'CREATE_POST' | 'UPDATE_POST' | 'CREATE_COMMENT' | 'UPDATE_COMMENT';

export interface IPostProps {
  isExpanded?: boolean;
  projectId: number;
  actionType: TAction;
  companyId: string; // Keep as string
  editablePost?: IPost;
  editableComment?: IPostComment;
  uploadPath?: string;
  replyToProfile?: {
    userId: string;
    displayName: string;
    avatarUrl?: string;
  };
  mentionOptions: ICustomMentionData[];
  onExpand?: (isExpanded: boolean) => void;
  setOpen?: (open: boolean) => void;
  onScrollToTop?: () => void;
  getCustomInputStyles?: (isError: boolean) => IStyles;
  onUpdatePost?: (body: Partial<IPost>) => void;
  onCreatePost?: (body: Partial<IPost>) => void;
  onUpdateComment?: (body: Partial<IPostComment>) => void;
  onCreateComment?: (body: Partial<IPostComment>) => void;
  onSaveDraftPost?: (body: any) => void;
}

// File upload configuration
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_FILE_COUNT = 10;

// Convert to comma-separated strings for attr-accept compatibility
const ACCEPTED_FILE_TYPES_STRING = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
  'video/webm',
  'audio/mpeg',
  'audio/wav',
  'audio/aac',
  'audio/ogg',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

const ACCEPTED_IMAGE_TYPES_STRING = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].join(
  ','
);

interface DropzoneWrapperProps {
  children: React.ReactNode;
  onFilesSelected: (files: File[]) => void;
  accept: string;
  maxFiles: number;
  maxSize: number;
}

const FileUploadDropzone: FC<DropzoneWrapperProps> = ({
  children,
  onFilesSelected,
  accept,
  maxFiles,
  maxSize,
}) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onFilesSelected,
    accept: accept,
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {children}
    </div>
  );
};

const ImageUploadDropzone: FC<DropzoneWrapperProps> = ({
  children,
  onFilesSelected,
  accept,
  maxFiles,
  maxSize,
}) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onFilesSelected,
    accept: accept,
    maxFiles,
    maxSize,
    multiple: true,
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {children}
    </div>
  );
};

const Post: FC<IPostProps> = ({
  isExpanded,
  projectId,
  actionType,
  companyId,
  editablePost,
  editableComment,
  uploadPath,
  replyToProfile,
  mentionOptions,
  onExpand,
  getCustomInputStyles,
  setOpen,
  onScrollToTop,
  onUpdatePost,
  onCreatePost,
  onUpdateComment,
  onCreateComment,
  onSaveDraftPost,
}) => {
  const project = useAtomValue(selectedProjectAtom);

  const emojiPlugin = useMemo(
    () =>
      createEmojiPlugin({
        useNativeArt: true,
        selectButtonContent: <EmojiIcon />,
        selectPopperOptions: {
          strategy: 'fixed',
          placement: 'bottom-start',
        },
        suggestionsPopperOptions: {
          strategy: 'fixed',
          placement: 'top',
        },
      }),
    []
  );
  const { EmojiSelect } = emojiPlugin;

  const { data: userMe } = useGetMeQuery();

  // Mock permissions - you'll need to replace this with actual permissions
  const userPermissions = {
    ACTIVITY_FEED_ALL_POSTS: { canUpdate: true },
    ENABLE_ACTIVITY_FEED: { canUpdate: true },
  };

  const existingContent = useMemo(
    () =>
      editablePost?.message
        ? editablePost.message
        : editableComment?.message
          ? editableComment.message
          : '',
    [editablePost?.message, editableComment?.message]
  );

  const [editorState, setEditorState] = useState(() => {
    if (
      actionType === 'CREATE_COMMENT' &&
      replyToProfile &&
      replyToProfile.userId !== userMe?.id.toString()
    ) {
      const mockProfile = {
        id: parseInt(replyToProfile.userId) || 1,
        userId: parseInt(replyToProfile.userId) || 1,
        firstName: replyToProfile.displayName?.split(' ')[0] || '',
        lastName: replyToProfile.displayName?.split(' ').slice(1).join(' ') || '',
        role: 'user',
        avatar: replyToProfile.avatarUrl || null,
      };
      const { mentionEntity, textContent } = createMentionToContentHelper(mockProfile);
      const draftReplyContent = createDraftContent({
        profiles: project.profiles,
        blocksContent: [textContent],
        mentions: [mentionEntity],
        hashtags: [],
      });
      return EditorState.createWithContent(convertFromRaw(draftReplyContent));
    }

    if (existingContent) {
      const mentions = editablePost?.mentions || editableComment?.mentions || [];
      const mentionObjects = mentions.map((mention, index) => ({
        profileId: mention.profileId || index + 1,
        length: mention.length,
        offset: mention.offset,
        block: mention.block,
        key: index,
      }));

      const draftContent = createDraftContent({
        profiles: project.profiles,
        blocksContent: convertHtml(existingContent),
        mentions: mentionObjects,
        hashtags: editablePost?.hashtags || editableComment?.hashtags || [],
      });

      return EditorState.createWithContent(convertFromRaw(draftContent));
    }

    return EditorState.createEmpty();
  });

  const rawEditorContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
  const sanitizedContent = rawEditorContent === '<p></p>\n' ? '' : rawEditorContent;

  const [postIdState, setPostIdState] = useState<string | null>(() => {
    if (actionType === 'CREATE_POST' && uploadPath) {
      const postRef = collection(db, uploadPath);
      const newPostRef = doc(postRef);
      return newPostRef.id;
    }
    return null;
  });

  const resetPostId = useCallback(() => {
    if (actionType === 'CREATE_POST' && uploadPath) {
      const postRef = collection(db, uploadPath);
      const newPostRef = doc(postRef);
      setPostIdState(newPostRef.id);
    }
  }, [actionType, uploadPath]);

  const postId = postIdState;

  // Update upload path to include the post ID
  const uploadPathWithPostId = useMemo(() => {
    if (postId && actionType === 'CREATE_POST') {
      return `${uploadPath}/${postId}`;
    }
    return uploadPath;
  }, [uploadPath, postId, actionType]);

  const { uploadMultipleFiles, getAllUploads, isUploading, clearAllUploads, clearUpload } =
    useFileUpload({
      uploadPath: uploadPathWithPostId,
      onUploadComplete: (result) => {
        console.log('Upload completed:', result);
      },
      onUploadError: (error, fileId) => {
        console.error('Upload error:', error);
      },
      onTranscodingComplete: (result) => {
        console.log('Transcoding completed:', result);
      },
    });

  // State for transcoding thumbnails (must be declared before uploadedFiles)
  const [transcodedThumbnails, setTranscodedThumbnails] = useState<Map<string, string>>(new Map());
  const [uploadTimestamps, setUploadTimestamps] = useState<Map<string, number>>(new Map());

  const uploadedFiles: IUploadingFiles[] = getAllUploads().map((upload) => {
    let status: StatusValue;
    if (upload.status === 'error') {
      status = 'error_file_size';
    } else if (upload.status === 'uploading') {
      status = 'uploading';
    } else if (upload.status === 'completed') {
      status = 'done';
    } else {
      status = 'uploading';
    }

    // Get video preview/thumbnail download URL from transcoded files (already fetched)
    const thumbnailUrl = transcodedThumbnails.get(upload.fileId);

    return {
      id: upload.fileId,
      url: upload.originalUrl || '',
      name: upload.fileName,
      tempId: upload.fileId,
      status,
      percent: upload.progress,
      size: 0, // We don't track size in the new system
      mimeType: getMimeTypeFromFileName(upload.fileName), // Add mime type for proper preview
      previewUrl: thumbnailUrl, // Full download URL for video thumbnails
    };
  });

  // const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmojiSelectorOpen, setIsEmojiSelectorOpen] = useState(false);
  // const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [searchParams] = useSearchParams();

  // Listen for transcoding job updates
  useEffect(() => {
    if (!companyId || !projectId) return;

    // Create a query to listen to transcoding jobs for this company/project
    const q = query(
      collection(db, 'transcodingJobs'),
      where('companyId', '==', companyId),
      where('projectId', '==', projectId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Get current uploaded files inside the callback to see latest additions
      const uploadedFiles = getAllUploads();

      snapshot.docChanges().forEach((change) => {
        if (change.type === 'modified') {
          const job = change.doc.data() as TranscodingJob;

          // Find matching uploaded file by fileName
          const matchingFile = uploadedFiles.find(
            (upload) => upload.fileName === job.originalFileName
          );

          // Check if the job was created after the upload started
          const uploadTimestamp = uploadTimestamps.get(matchingFile?.fileId || '');
          const jobCreatedAt = job.createdAt?.toMillis?.() || job.createdAt?.seconds * 1000 || 0;
          const uploadStartedAfter = uploadTimestamp ? jobCreatedAt >= uploadTimestamp : false;

          // Only process if: matching file exists, job completed, job created after upload started
          const shouldProcess =
            matchingFile &&
            job.status === 'completed' &&
            job.transcodedFiles &&
            (uploadTimestamp === undefined || uploadStartedAfter);

          if (shouldProcess) {
            // Find the thumbnail
            const thumbnail = job.transcodedFiles.find((file) => file.quality === 'thumbnail');

            if (thumbnail) {
              // Fetch the download URL for the thumbnail
              const storageRef = ref(storage, thumbnail.path);
              getDownloadURL(storageRef)
                .then((downloadURL) => {
                  // Store the download URL
                  setTranscodedThumbnails((prev) => {
                    const updated = new Map(prev);
                    updated.set(matchingFile.fileId, downloadURL);
                    return updated;
                  });
                })
                .catch((error) => {
                  console.error('Error fetching thumbnail download URL:', error);
                });
            }
          }
        }
      });
    });

    return () => unsubscribe();
  }, [companyId, projectId, getAllUploads, uploadTimestamps]);

  // Track upload timestamps for each new upload
  useEffect(() => {
    const uploads = getAllUploads();
    uploads.forEach((upload) => {
      if (!uploadTimestamps.has(upload.fileId)) {
        // New upload detected, record timestamp
        setUploadTimestamps((prev) => {
          const updated = new Map(prev);
          updated.set(upload.fileId, Date.now());
          return updated;
        });
      }
    });
  }, [getAllUploads, uploadTimestamps]);

  const form = useForm({
    resolver: yupResolver(
      actionType === 'CREATE_POST' || actionType === 'UPDATE_POST'
        ? activityFeedSchema
        : postReplySchema
    ),
    defaultValues: {
      content: '',
    },
  });

  const hasPendingUploads =
    isUploading || uploadedFiles.some((file) => file.status === 'uploading');

  // const savePostAsDraft = useCallback(
  //   async (draftContent: string) => {
  //     try {
  //       if (!draftContent || draftContent === '<p></p>') {
  //         return; // Don't save empty drafts
  //       }

  //       setIsDraftSaving(true);

  //       const draftPost: Partial<FilePost> = {
  //         projectId: projectId || '',
  //         companyId: companyId,
  //         fileId: fileId,
  //         fileName: fileName,
  //         message: draftContent,
  //         mentions: [],
  //         // visibility: 'public',
  //         media: [],
  //         tags: [],
  //         likes: [],
  //         // isLocalDraft: true,
  //         createdAt: new Date() as any, // Firestore will convert this to Timestamp
  //         createdBy: {
  //           userId: userMe?.id.toString() || '',
  //           displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
  //           avatarUrl: userMe?.avatar || '',
  //         },
  //         // commentsCount: 0,
  //       };

  //       // If we already have a draft, update it instead of creating a new one
  //       if (currentDraftId) {
  //         // Use the update atom to modify the existing draft
  //         await onUpdatePost?.({
  //           id: currentDraftId,
  //           ...draftPost,
  //         });
  //       } else {
  //         // Create a new draft using the create atom
  //         await onCreatePost?.(draftPost);

  //         // Since onCreatePost returns void, generate a temporary ID for tracking this draft
  //         const tempDraftId = `temp_draft_${Date.now()}`;
  //         setCurrentDraftId(tempDraftId);
  //       }
  //     } catch (error) {
  //       console.error('❌ Error saving draft:', error);
  //     } finally {
  //       setIsDraftSaving(false);
  //     }
  //   },
  //   [projectId, companyId, fileId, userMe, onCreatePost, onUpdatePost, currentDraftId]
  // );

  // const publishDraft = useCallback(
  //   async (post: Partial<FilePost>) => {
  //     try {
  //       if (!post.message || post.message === '<p></p>') {
  //         return; // Don't publish empty posts
  //       }

  //       const publishedPost: Partial<FilePost> = {
  //         projectId: projectId || '',
  //         companyId: companyId,
  //         fileId: fileId,
  //         fileName: fileName,
  //         message: post.message,
  //         mentions: post.mentions,
  //         // visibility: 'public',
  //         media: uploadedFiles.map((file) => ({
  //           type: file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image' : 'file',
  //           url: file.url,
  //           name: file.name,
  //           description: '', // IUploadingFiles doesn't have description property
  //         })),
  //         tags: [],
  //         likes: [],
  //         // isLocalDraft: false, // Mark as published
  //         createdAt: new Date() as any,
  //         createdBy: {
  //           userId: userMe?.id.toString() || '',
  //           displayName: `${userMe?.firstName} ${userMe?.lastName}` || '',
  //           avatarUrl: userMe?.avatar || '',
  //         },
  //         // commentsCount: 0,
  //       };

  //       if (currentDraftId) {
  //         // Update the existing draft to make it published
  //         await onUpdatePost?.({
  //           id: currentDraftId,
  //           ...publishedPost,
  //         });

  //         // Clear the draft ID since it's now published
  //         setCurrentDraftId(null);
  //       } else {
  //         // Create a new published post
  //         try {
  //           await onCreatePost?.(publishedPost);
  //         } catch (error) {
  //           console.error('❌ Error publishing post:', error);
  //           throw error;
  //         }
  //       }

  //       // Clear the form after successful submission
  //       setEditorState(EditorState.createEmpty());
  //       setUploadedFiles([]);

  //       // Collapse if it's a post
  //       if (actionType === 'CREATE_POST') {
  //         onExpand?.(false);
  //       }
  //     } catch (error) {
  //       console.error('❌ Error publishing post:', error);
  //       throw error;
  //     } finally {
  //       setIsSubmitting(false);
  //     }
  //   },
  //   [
  //     projectId,
  //     companyId,
  //     fileId,
  //     userMe,
  //     onCreatePost,
  //     onUpdatePost,
  //     currentDraftId,
  //     uploadedFiles,
  //     actionType,
  //     onExpand,
  //   ]
  // );

  // Wrapper function for ClickAwayOnceListener that extracts current editor content

  const handleDraftSaveOnBlur = useCallback(() => {
    const currentContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    if (currentContent && currentContent !== '<p></p>') {
      onSaveDraftPost(currentContent);
    }
  }, [editorState, onSaveDraftPost]);

  // Auto-save draft every 30 seconds while user is typing
  // useEffect(() => {
  //   if (!isExpanded || !editorState.getCurrentContent().hasText()) {
  //     return;
  //   }

  //   const autoSaveInterval = setInterval(() => {
  //     const currentContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
  //     if (currentContent && currentContent !== '<p></p>') {
  //       onSaveDraftPost(currentContent);
  //     }
  //   }, 30000); // 30 seconds

  //   return () => clearInterval(autoSaveInterval);
  // }, [isExpanded, editorState, onSaveDraftPost]);

  // // Clean up draft ID when component unmounts or editor is collapsed
  // useEffect(() => {
  //   if (!isExpanded) {
  //     setCurrentDraftId(null);
  //   }
  // }, [isExpanded]);

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      try {
        await uploadMultipleFiles(files);
      } catch (error) {
        console.error('File upload failed:', error);
      }
    },
    [uploadMultipleFiles]
  );

  const handleDeleteUploadedData = useCallback(
    (fileId: string) => {
      console.log('Delete requested for file:', fileId);
      clearUpload(fileId);
      // Clear the thumbnail and timestamp if they exist
      setTranscodedThumbnails((prev) => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });
      setUploadTimestamps((prev) => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });
    },
    [clearUpload]
  );

  const handleBlurInput = useCallback(() => {
    // Handle blur logic if needed
  }, []);

  const onOpenEmojiSelect = useCallback(() => {
    setIsEmojiSelectorOpen(true);
  }, []);

  const onCloseEmojiSelect = useCallback(() => {
    setIsEmojiSelectorOpen(false);
  }, []);

  const handleCollapseInput = useCallback(() => {
    onExpand?.(false);
  }, [onExpand]);

  const onSubmit = async () => {
    // setIsSubmitting(true);
    const rawDraftContent = convertToRaw(editorState.getCurrentContent());
    const mentions = createMentionsArray(rawDraftContent) || [];
    const hashtags = getHashtagsArray(rawDraftContent) || [];

    const completedUploads = getAllUploads().filter(
      (upload) => upload.status === 'completed' && upload.originalUrl
    );

    if (actionType === 'CREATE_COMMENT') {
      onCreateComment({
        message: draftToHtml(convertToRaw(editorState.getCurrentContent())),
        mentions,
        hashtags,
      });
      clearAllUploads();
      // setIsSubmitting(false);
    } else if (actionType === 'UPDATE_COMMENT') {
      onUpdateComment({
        ...editableComment,
        message: draftToHtml(convertToRaw(editorState.getCurrentContent())),
        mentions,
        hashtags,
      });
      clearAllUploads();
      // setIsSubmitting(false);
    } else if (actionType === 'UPDATE_POST') {
      onUpdatePost({
        ...editablePost,
        message: draftToHtml(convertToRaw(editorState.getCurrentContent())),
        mentions,
        hashtags,
        media: [
          ...(editablePost?.media || []),
          ...uploadedFiles.map((file) => ({
            type: file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image' : 'file',
            url: file.url,
            name: file.name,
          })),
        ] as IMediaItem[],
      });
      clearAllUploads();
      // setIsSubmitting(false);
    } else {
      try {
        const content = draftToHtml(convertToRaw(editorState.getCurrentContent()));

        if (!content || content === '<p></p>') {
          setOpen?.(false);
          return;
        }

        const postToCreate: Partial<IPost> = {
          id: postId, // Use the same ID as the upload path
          message: content,
          mentions,
          hashtags,
          media: uploadedFiles.map((file) => {
            return {
              id: file.id,
              type: (file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? 'image' : 'file') as
                | 'image'
                | 'file',
              url: file.url,
              name: file.name,
            };
          }),
        };

        onCreatePost(postToCreate);

        clearAllUploads();
        resetPostId(); // Generate a new post ID for the next post
        onExpand?.(false);
      } catch (error) {
        console.error('❌ Error submitting post:', error);
      }
    }
    setOpen?.(false);
    setEditorState(EditorState.createEmpty());
  };

  const isError = useMemo(() => !!form.formState.errors?.content, [form.formState.errors?.content]);

  const inputStyles = useMemo(
    () => ({
      '.mnw6qvm': {
        borderRadius: '12px',
        marginTop: '12px',
        filter:
          'drop-shadow(0px 4px 10px rgba(35, 37, 47, 0.1)) drop-shadow(1px 2px 2px rgba(35, 37, 47, 0.04))',
        boxShadow: 'none',
      },
      '.DraftEditor-root': {
        height: '195px',
        minHeight: 'unset',
      },
      '.DraftEditor-editorContainer': {
        height: '195px',
        minHeight: 'unset',
        overflowY: 'auto',
        borderRadius: '12px',
        outline: isError ? '1px solid #d32f2f' : '1px solid #EDEFF7',
        fontFamily:
          "'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Twemoji', sans-serif",
        div: { boxSizing: 'border-box' },
        '&:hover': {
          outline: '1px solid #EDEFF7',
        },
        '&:focus-within': {
          outline: '1px solid #EDEFF7',
        },
      },
    }),
    [isError]
  );

  const postDataForUpdate: Partial<IPost> = editablePost
    ? {
        message: editablePost.message,
        media: editablePost.media,
      }
    : null;

  // Create a mock object for the UploadedImages component that expects IUpdatePostBody
  const mockEditablePostBody = useMemo(() => {
    if (!editablePost) return undefined;

    return {
      id: parseInt(editablePost.id || '0') || 0,
      projectId: parseInt(editablePost.projectId || '0') || 0,
      content: editablePost.message || '',
      fileId: editablePost.itemId || '',
      fileName: editablePost.itemName || '',
      uploads:
        editablePost.media?.map((media) => ({
          id: media.url,
          description: media.description || null,
          location: null,
          timestamp: null,
        })) || [],
      phaseIds: [],
      phaseItemIds: [],
      audienceGroups: [],
      audienceRoles: [],
      postVisibility: 'EVERYONE' as any,
      mentions: [],
      visibleProfileIds: [],
      status: 'PUBLISHED' as any,
      wasPreviouslyDraft: false,
    };
  }, [editablePost]);

  const isDescriptionEditable =
    editablePost?.createdBy?.userId === userMe?.userId?.toString() ||
    (userPermissions?.ACTIVITY_FEED_ALL_POSTS?.canUpdate &&
      userPermissions?.ENABLE_ACTIVITY_FEED?.canUpdate) ||
    actionType === 'CREATE_POST';

  useEffect(() => {
    const content = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    form.setValue('content', content, {
      shouldDirty: true,
    });
  }, [editorState, form.setValue]);

  useDeleteUnsupportedEmojies(isEmojiSelectorOpen);

  const initials = `${userMe?.firstName?.[0] || 'U'}${userMe?.lastName?.[0] || 'S'}`;

  const isPost = ['CREATE_POST', 'UPDATE_POST'].includes(actionType);

  return (
    <>
      {isExpanded && (
        <FormProvider {...form}>
          <Grid sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Grid
              component="form"
              onSubmit={form.handleSubmit(onSubmit)}
              sx={{
                display: 'flex',
                width: '100%',
              }}
            >
              <Collapse sx={{ display: 'flex', width: '100%' }} in={true}>
                <Grid sx={newPostStyles.expandedInputContainer}>
                  {actionType === 'CREATE_POST' ? (
                    <Grid sx={newPostStyles.minimizeBtnContainer}>
                      <Button sx={newPostStyles.minimizeBtn} onClick={handleCollapseInput}>
                        <ChevronIcon
                          style={{
                            height: '21px',
                            width: '24px',
                          }}
                        />
                      </Button>
                    </Grid>
                  ) : null}
                  <Grid
                    sx={{
                      ...newPostStyles.expandedInput,
                      padding: actionType === 'CREATE_POST' ? '16px' : '0',
                      backgroundColor: actionType === 'CREATE_POST' ? '#FAFBFC' : '#FFFFFF',
                    }}
                  >
                    {/* {showAudience && (
                      <AudienceContainer
                        projectId={project?.id}
                        projectProfiles={project?.profiles}
                        projectAudiences={convertedSpecificUsers}
                        postAudience={convertedLatestAudience}
                        isLoading={!convertedLatestAudience}
                        isError={isAudienceError}
                        onSetLatestPostAudience={setLatestPostAudience}
                      />
                    )} */}
                    <Grid sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                      {/* <ClickAwayOnceListener onAway={handleDraftSaveOnBlur} key={`${isExpanded}`}> */}
                      <Grid sx={newPostStyles.textEditorContainer}>
                        {isPost && (
                          <MentionIndicator
                            hide={!!sanitizedContent}
                            sx={newPostStyles.mentionIndicator}
                          />
                        )}
                        <TextEditor
                          projectId={projectId}
                          autofocus={true}
                          placeholder={
                            actionType === 'CREATE_COMMENT' || actionType === 'UPDATE_COMMENT'
                              ? 'Leave a comment…'
                              : 'Write your message…'
                          }
                          editorState={editorState}
                          isError={isError}
                          inputStyles={
                            getCustomInputStyles ? getCustomInputStyles(isError) : inputStyles
                          }
                          onChange={setEditorState}
                          onBlur={handleBlurInput}
                          onSubmit={form.handleSubmit(onSubmit)}
                          setUploadedFiles={() => {}}
                          mentionsList={mentionOptions}
                          saveHeight={
                            actionType === 'CREATE_COMMENT' &&
                            !replyToProfile &&
                            searchParams.has('postId')
                          }
                          emojiPlugin={emojiPlugin}
                        />
                      </Grid>
                      {/* </ClickAwayOnceListener> */}
                      {/* {actionType === 'CREATE_POST' || actionType === 'UPDATE_POST' ? (
                        <Grid sx={newPostStyles.tagsContainer}>
                          {selectedTags?.length
                            ? selectedTags
                                .slice(0, 2)
                                .map((tag) => <Tag key={tag.id} option={tag} bgColor="#FFF" />)
                            : null}
                          {selectedTags?.length && selectedTags?.length > 2 ? (
                            <Tag
                              key={0}
                              bgColor="#FFF"
                              option={{ id: 0, title: `+${selectedTags.length - 2} more` }}
                            />
                          ) : null}
                          <AddTagButton
                            phases={project.phases}
                            phaseValues={phaseValues}
                            phaseItemsValues={phaseItemsValues}
                            setPhaseValues={setPhaseValues}
                            setPhaseItemsValues={setPhaseItemsValues}
                            handleSubmitTags={handleSubmitTags}
                            handleSetDefaultTags={handleSetDefaultTags}
                          />
                        </Grid>
                      ) : null} */}
                      {uploadedFiles.length > 0 ? (
                        <Grid sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                          <UploadedImages
                            type="new_images"
                            containerStyles={{
                              width: '100%',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '12px',
                              flexDirection: 'row',
                              marginTop: '12px',
                            }}
                            uploadingFiles={uploadedFiles}
                            handleDeleteUploadedData={handleDeleteUploadedData}
                            isDescriptionEdtable={isDescriptionEditable}
                          />
                          <UploadedFiles
                            type="new_files"
                            uploadingFiles={uploadedFiles}
                            handleDeleteUploadedData={handleDeleteUploadedData}
                          />
                        </Grid>
                      ) : null}
                    </Grid>
                    <Grid sx={newPostStyles.actionsContainer}>
                      <Grid sx={newPostStyles.actionBtnsContainer}>
                        {uploadPath && (
                          <>
                            <FileUploadDropzone
                              onFilesSelected={handleFileUpload}
                              accept={ACCEPTED_FILE_TYPES_STRING}
                              maxFiles={MAX_FILE_COUNT}
                              maxSize={MAX_FILE_SIZE}
                            >
                              <IconButton component="label">
                                <AttachFileIcon />
                              </IconButton>
                            </FileUploadDropzone>

                            <ImageUploadDropzone
                              onFilesSelected={handleFileUpload}
                              accept={ACCEPTED_IMAGE_TYPES_STRING}
                              maxFiles={MAX_FILE_COUNT}
                              maxSize={MAX_FILE_SIZE}
                            >
                              <IconButton component="label">
                                <AttachImageIcon />
                              </IconButton>
                            </ImageUploadDropzone>
                          </>
                        )}
                        <EmojiSelect onClose={onOpenEmojiSelect} onOpen={onCloseEmojiSelect} />
                      </Grid>
                      {actionType === 'CREATE_POST' ? (
                        <Button
                          disabled={hasPendingUploads || isDraftSaving}
                          type="submit"
                          sx={newPostStyles.submitBtn}
                        >
                          {isDraftSaving ? 'Saving...' : 'Publish'}
                        </Button>
                      ) : (
                        <Grid
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            gap: '12px',
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setOpen?.(false);
                            }}
                            sx={newPostStyles.cancelBtn}
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={hasPendingUploads || isDraftSaving}
                            type="submit"
                            sx={newPostStyles.submitBtn}
                          >
                            {actionType === 'CREATE_COMMENT'
                              ? isDraftSaving
                                ? 'Saving...'
                                : 'Post'
                              : 'Save'}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        </FormProvider>
      )}

      {!isExpanded && (
        <Grid
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'row',
            backgroundColor: '#F4F5F9',
            gap: '12px',
            padding: '12px 20px',
          }}
        >
          {userMe?.avatar ? (
            <Avatar
              alt="avatar"
              sx={{
                width: '36px',
                height: '36px',
                borderRadius: '7.2px',
              }}
              src={getImageBySize(userMe?.avatar, 36, 36)}
              variant="square"
            />
          ) : (
            <DefaultAvatar
              initials={initials}
              variant="square"
              sx={{
                width: '36px',
                height: '36px',
                borderRadius: '7.2px',
              }}
            />
          )}
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 12px',
              cursor: 'pointer',
              width: '100%',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
            }}
            onClick={() => onExpand?.(true)}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                color: '#8C8F9D',
                fontSize: '14px',
                fontStyle: 'normal',
                lineHeight: '140%',
                fontWeight: '400',
                '-webkit-user-select': 'none',
                '-ms-user-select': 'none',
                'user-select': 'none',
              }}
            >
              Start a new discussion...
            </Typography>
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                '& svg': {
                  width: '20px',
                  height: '20px',
                },
              }}
            >
              {uploadPath && <AttachFileIcon />}
              {uploadPath && <AttachImageIcon />}
              <EmojiIcon />
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default Post;
